'use client';

import React, { createContext, useContext, useState } from 'react';
import IndustryRepository from '@dm-portal/industry/IndustryRepository';
import { getContainer } from '@framework/di/getContainer';
import { IIndustry } from '@dm-portal/industry/Industry';

interface ISubIndustry {
  industryName: string,
  subIndustryName: string,
  industryColor: string,
  industryAbbreviation: string,
}

const useIndustry = () => {
  const container = getContainer();
  const [subIndustryList, setSubIndustryList] = useState<Map<string, ISubIndustry>>(new Map<string, ISubIndustry>());

  const getIndustryList = async (): Promise<void> => {
    const industryRepository = container.get<IndustryRepository>(IndustryRepository);
    const payload: IIndustry[] = await industryRepository.list();

    const subIndustryList = new Map<string, ISubIndustry>();

    payload.forEach(
      (industry) => industry.subindustryList.forEach(
        (subIndustry) => {
          subIndustryList.set(subIndustry.id, {
            industryName: industry.name,
            subIndustryName: subIndustry.name,
            industryColor: industry.color,
            industryAbbreviation: industry.abbreviation,
          });
        },
      ),
    );

    setSubIndustryList(subIndustryList);
  };

  return {
    subIndustryList,
    getIndustryList,
  };
};

type IndustryContextType = ReturnType<typeof useIndustry>;

export const IndustryContext = createContext<IndustryContextType | null>(null);

export const useIndustryContext = () => {
  const context = useContext(IndustryContext);
  if (!context) {
    throw new Error('useIndustryContext must be used within a IndustryContextProvider');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const IndustryContextProvider = ({ children }: IProps) => (
  <IndustryContext.Provider value={ useIndustry() }>
    { children }
  </IndustryContext.Provider>
);

export default IndustryContextProvider;
